import { useCallback } from "react"
import { useMutation, useQuery } from "@tanstack/react-query"

import { updateAssignments } from "@/services/assignments"
import { updateSocialUsers } from "@/components/SocialMediaInput/queries"
import { sendUserActionPost } from "shared/user-actions/user-actions-action-creators"
import { formatTagDictMutation, trimStringValues } from "@/pages/helpers"

import type { QueryFunctionContext } from "@tanstack/react-query"
import type { MutateFnArgs, FormValues } from "@/pages/edit-contact/types"
import type { CustomField } from "@/types/djangio"

const quorumDataTypeEnum = DjangIO.app.models.QuorumDataType.supporter

// takes a supporter ID aka an entity ID, and returns a unique query key for the Supporter
export const supporterKeys = {
    all: [{ _model: "supporter" }] as const,
    detail: (entityId: string, dehydrateExtra?: string) =>
        [{ ...supporterKeys.all[0], entityId, dehydrateExtra }] as const,
}

const fetchSupporter = async ({
    queryKey,
}: QueryFunctionContext<ReturnType<(typeof supporterKeys)["detail"]>>): Promise<QuorumModel["NewSupporter"]> => {
    const [{ entityId, dehydrateExtra }] = queryKey

    const response = await DjangIO.app.grassroots.models.NewSupporter.objects
        .filter({ dehydrate_extra: dehydrateExtra })
        .get(entityId)
        .GET()

    return response
}

export const useSupporter = (entityId: string, dehydrateExtra?: string) =>
    useQuery({
        queryKey: supporterKeys.detail(entityId, dehydrateExtra),
        queryFn: fetchSupporter,
        // conditionally enables/disables query, depending on if we're working
        // with an existing Supporter or creating a brand new one
        enabled: Boolean(entityId),
        // "select" extracts and transforms data from the query result before returning it
        select: useCallback((queryResultData: QuorumModel["NewSupporter"]) => {
            const limitedToTeams: number[] = queryResultData.limited_to_teams.map((teamUri: string) =>
                DjangIO.app.userdata.models.Team.idFromResourceUri(teamUri),
            )

            const orgIds: number[] = queryResultData.public_organizations.map((orgUri: string) =>
                DjangIO.app.person.models.PublicOrganization.idFromResourceUri(orgUri),
            )

            const ledgerSettingsIds: number[] = queryResultData.pac_eligibilities.map((ledgerSettingsUri: string) =>
                DjangIO.app.ledger.models.LedgerSettings.idFromResourceUri(ledgerSettingsUri),
            )

            const supporterTypeGroup: () => string[] = () => {
                const supporterTypes: string[] = []
                if (queryResultData.is_grassroots_supporter) {
                    supporterTypes.push("grassroots")
                }
                if (queryResultData.is_stakeholder_supporter) {
                    supporterTypes.push("stakeholder")
                }
                if (queryResultData.is_collaborator_supporter) {
                    supporterTypes.push("collaborator")
                }
                return supporterTypes
            }

            const tagDict = Object.entries(queryResultData.tag_dict).reduce(
                (acc, [key, value]: [string, any]) => {
                    if (key.includes("date_cf") && value) {
                        acc[key] = new Date(`${value}T00:00:00`)
                    } else {
                        acc[key] = value
                    }
                    return acc
                },
                {} as Record<string, string | number | Date | Array<string>>,
            )

            const transformedQueryResultData = {
                // Basic Fields step
                alternateAddress: queryResultData?.alternate_address,
                charityName: queryResultData?.pac_charity_name,
                charityEIN: queryResultData?.pac_charity_ein,
                charityAddress: queryResultData?.pac_charity_address,
                charitySelectionDate: queryResultData?.pac_charity_selection_date
                    ? new Date(queryResultData?.pac_charity_selection_date).toLocaleDateString()
                    : "",
                email: queryResultData?.email || "",
                employer: queryResultData?.employer,
                extension: queryResultData?.extension,
                firstName: queryResultData?.firstname,
                inputAddress: queryResultData?.address,
                lastName: queryResultData?.lastname,
                middleName: queryResultData?.middlename,
                occupation: queryResultData?.occupation,
                orgs: orgIds,
                pacConditionalGivingLevel: queryResultData?.pac_conditional_giving_level,
                pacEligibilities: ledgerSettingsIds,
                pacHybridEligible: queryResultData?.pac_hybrid_eligible,
                phoneNumber: queryResultData?.phone_number || "",
                preferredName: queryResultData?.preferred_name,
                prefix: queryResultData?.prefix,
                profileUpload: queryResultData?.image_url,
                secondaryEmail: queryResultData?.secondary_email || "",
                secondaryExtension: queryResultData?.secondary_extension,
                secondaryPhone: queryResultData?.secondary_phone || "",
                shouldDispLayInExternalLogger: queryResultData?.should_display_in_external_logger,
                suffix: queryResultData?.suffix,
                supporterTypeGroup: supporterTypeGroup(),
                title: queryResultData?.title,
                uniqueIdentifier: queryResultData?.unique_identifier,
                unsubscribedFromBulkEmails: queryResultData?.unsubscribed_from_bulk_emails,
                // Custom Fields step
                tagDict,
                // Permissions step
                limitedToTeams,
                teamPermissioning: queryResultData.limited_to_teams.length ? "limited_to_teams" : "everyone",
                // PAC Individuals Information
                _extra: {
                    individuals_information: queryResultData._extra?.individuals_information || {
                        email: "",
                        full_home_address: "",
                        full_work_address: "",
                        pac_email: "",
                        preferred_email: "",
                        work_email: "",
                        home_phone: "",
                    },
                },
            }

            return transformedQueryResultData
        }, []),
    })

const getBase64BusinessCard = (businessCard) => {
    const reader = new FileReader()

    return new Promise((resolve) => {
        reader.onload = (e) => resolve((e.target.result as string).split(",")[1]) // remove "data:*/*;base64," from the result
        reader.readAsDataURL(businessCard)
    })
}

const postBusinessCard = async ({ businessCard, businessCardBack, supporterId }) => {
    const pictures = await Promise.all([
        getBase64BusinessCard(businessCard),
        ...(businessCardBack ? [getBase64BusinessCard(businessCardBack)] : []),
    ])

    const data = {
        supporter: DjangIO.app.grassroots.models.Supporter.foreignKey(supporterId),
        supporter_id: supporterId,
        pictures,
    }

    return DjangIO.app.grassroots.models.BusinessCard.objects.create(data).POST()
}

const saveSupporter = async ({
    customFields,
    values,
    entityId,
}: {
    customFields: CustomField[]
    values: FormValues
    entityId?: string
}) => {
    const isGrassrootsSupporter = () => {
        if (values.supporterTypeGroup === undefined) {
            return false
        }
        return values.supporterTypeGroup.includes("grassroots")
    }
    const isStakeholderSupporter = () => {
        if (values.supporterTypeGroup === undefined) {
            return false
        }
        return values.supporterTypeGroup.includes("stakeholder")
    }
    const isCollaboratorSupporter = () => {
        if (values.supporterTypeGroup === undefined) {
            return false
        }
        return values.supporterTypeGroup.includes("collaborator")
    }

    const getLimitedToTeams = () => {
        if (values.teamPermissioning === "everyone") {
            return []
        }
        return values.limitedToTeams.map((team) =>
            typeof team === "number" ? DjangIO.app.userdata.models.Team.resourceUriFromId(team) : team.resource_uri,
        )
    }

    const businessCardData = values.businessCard && {
        firstname: "[PROCESSING]",
        lastname: "[BUSINESS CARD]",
        source_type: DjangIO.app.grassroots.types.UTMSourceType.business_card_scanner.value,
    }

    const data: Partial<QuorumModel["NewSupporter"]> = {
        alternate_address: values.alternateAddress,
        email: values.email,
        employer: values.employer,
        extension: values.extension,
        firstname: values.firstName || businessCardData?.firstname,
        input_address: values.inputAddress,
        is_grassroots_supporter: isGrassrootsSupporter(),
        is_stakeholder_supporter: isStakeholderSupporter(),
        is_collaborator_supporter: isCollaboratorSupporter(),
        lastname: values.lastName || businessCardData?.lastname,
        limited_to_teams: getLimitedToTeams(),
        middlename: values.middleName,
        occupation: values.occupation,
        public_organizations: values.orgs.map((org) =>
            typeof org === "number"
                ? DjangIO.app.person.models.PublicOrganization.resourceUriFromId(org)
                : org.resource_uri,
        ),
        // for when the pacEligibilities input is rendered
        pac_eligibilities: values.pacEligibilities?.map((ledgerSettings) =>
            typeof ledgerSettings === "number"
                ? DjangIO.app.ledger.models.LedgerSettings.resourceUriFromId(ledgerSettings)
                : ledgerSettings.resource_uri,
        ),
        pac_hybrid_eligible: values.pacHybridEligible,
        pac_conditional_giving_level: values.pacConditionalGivingLevel,
        phone_number: values.phoneNumber,
        preferred_name: values.preferredName,
        prefix: values.prefix,
        image_url: values.profileUpload,
        secondary_email: values.secondaryEmail,
        secondary_extension: values.secondaryExtension,
        secondary_phone: values.secondaryPhone,
        should_display_in_external_logger: values.shouldDispLayInExternalLogger,
        suffix: values.suffix,
        tag_dict: formatTagDictMutation(values.tagDict, customFields) as Record<string, string | number | string[]>,
        title: values.title,
        unique_identifier: values.uniqueIdentifier,
        unsubscribed_from_bulk_emails: values.unsubscribedFromBulkEmails,
        ...(!entityId && businessCardData && { source_type: businessCardData.source_type }),
    }

    // If input_address is empty, also set address to be empty, so the backend knows we want to clear it out
    if (values.inputAddress === "") {
        data["address"] = values.inputAddress
    }

    const formattedData = trimStringValues(data)

    // update existing Supporter
    if (entityId) {
        return DjangIO.app.grassroots.models.NewSupporter.objects
            .full_dehydrate("last_user")
            .filter({
                full_dehydrate_public_organization: true,
                full_dehydrate_public_organizations: true,
            })
            .get(entityId)
            .update(formattedData)
            .PATCH()
    }

    // create new Supporter
    return DjangIO.app.grassroots.models.NewSupporter.objects.full_dehydrate("last_user").create(formattedData).POST()
}

// Mutation hook that wraps mutationFn. Returns object containing a "mutate" Fn
// to call in form page to trigger the mutation. When it's called, the cache
// will be updated with the new data. isLoading, isError, isSuccess returned by
// useMutation hook to handle the result. Also can define callback functions to
// handle these states/update the UI.
export const useMutateSupporter = (customFields: CustomField[], onSuccess: () => void, entityId?: string) =>
    useMutation({
        mutationFn: async ({ values, assignments, socialMediaInput }: MutateFnArgs) => {
            const supporter = await saveSupporter({ customFields, values, entityId })

            const socialUsersRequest = updateSocialUsers({
                quorumDataTypeValue: quorumDataTypeEnum.value,
                newAccounts: socialMediaInput.newAccounts,
                relatedFieldKey: "supporter_id",
                entityId: supporter.id,
                toDelete: socialMediaInput.toDelete,
            })

            const assignmentUpdatingData = {
                values,
                assignments,
                entityId: supporter.id,
                assignmentType: DjangIO.app.userdata.types.AssignmentType.supporter,
                quorumDataTypeEnum: quorumDataTypeEnum,
            }

            const assignmentsRequest = updateAssignments(assignmentUpdatingData)

            await Promise.allSettled([socialUsersRequest, assignmentsRequest])

            if (values.businessCard)
                await postBusinessCard({
                    businessCard: values.businessCard,
                    businessCardBack: values.businessCardBack,
                    supporterId: supporter.id,
                })

            sendUserActionPost({
                userActionType: DjangIO.app.useractions.types.UserActionType.profile_edited,
                quorumDataType: quorumDataTypeEnum.value,
                regions: [supporter.region],
            })

            return supporter
        },
        onSuccess,
    })
